import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLayerGroup, faTag } from "@fortawesome/free-solid-svg-icons";
import { TriangleAlert, ChevronRight } from "lucide-react";
import { getIncidentAiInsights, getVerticalAiInsights } from "../../utils/verticalEventHandling";

const DEFAULT_ERROR = "Failed to load AI insights.";
const NOT_AVAILABLE_ERROR = "AI insights are not available for this incident.";
const TIMEOUT_ERROR = "QualdoAI is still generating insights for this incident. Please try again in a minute.";
// First-time insights run LLM reasoning on the server; the browser fetch itself has no timeout.
const REQUEST_TIMEOUT_MS = 120000;
const SLOW_NOTICE_AFTER_SEC = 5;
const SNAKE_CASE_RE = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/;
const INLINE_TOKEN_RE = /(\*\*[^*]+\*\*|`[^`]+`|\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\b)/g;

// Successful responses are kept for the session so reopening a popover does not call the LLM again.
const insightsCache = new Map();
// In-flight requests are shared so closing and reopening a popover keeps waiting on the same generation.
const pendingRequests = new Map();
// Underlying API calls, kept past a UI timeout so Retry re-attaches instead of starting another generation.
const inflightCalls = new Map();

const toId = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : value;
};

/**
 * Builds the incident_ai_insights payload for an incident row.
 * Incident types 1/2 are data quality incidents and anything else typed is a pipeline incident,
 * matching the like/ignore/read actions on the incidents page.
 */
export function getIncidentAiInsightsRequest(item) {
    if (!item) return null;
    if (item.pipeline_incident_id) return { pipeline_incident_id: toId(item.pipeline_incident_id) };
    if (item.dataquality_incident_id) return { dataquality_incident_id: toId(item.dataquality_incident_id) };

    const id = item.incident_id ?? item.kpi_incident_id ?? item.id;
    if (id === null || id === undefined || id === "") return null;

    const hasType = item.type !== null && item.type !== undefined;
    const isPipeline = hasType && item.type !== 1 && item.type !== 2;
    return isPipeline ? { pipeline_incident_id: toId(id) } : { dataquality_incident_id: toId(id) };
}

/**
 * Builds a getVerticalAiInsights request for a vertical (KPI) incident.
 * Critical incidents use GET; other levels POST with incident_level.
 */
export function getVerticalAiInsightsRequest(item, teamId, userId) {
    const incidentId = item?.kpi_incident_id;
    if (incidentId === null || incidentId === undefined || incidentId === "") return null;
    const incidentLevel = item?.incident_level;
    const params = {
        tenant_id: String(teamId),
        user_id: String(userId),
        incident_id: incidentId,
    };
    if (incidentLevel !== "Critical" && incidentLevel) params.incident_level = incidentLevel;
    return { vertical: true, method: incidentLevel === "Critical" ? "GET" : "POST", params };
}

const toList = (value) => {
    if (Array.isArray(value)) {
        return value
            .map((v) => (typeof v === "string" ? v : v?.text || v?.description || v?.content || ""))
            .map((v) => v.trim())
            .filter(Boolean);
    }
    if (typeof value === "string" && value.trim()) {
        return value.split(/\n{2,}/).map((v) => v.trim()).filter(Boolean);
    }
    return [];
};

// root_cause.evidence is one string with a fact per line ("\n"-separated).
const toLines = (value) => {
    if (typeof value === "string") return value.split(/\n+/).map((v) => v.trim()).filter(Boolean);
    return toList(value);
};

// Reasoning shape: { root_cause: { summary, description, evidence, affected_pipeline, affected_stage },
//                    impact_summary, recommendation, contributing_source }
export function normalizeIncidentAiInsights(payload) {
    if (typeof payload === "string" && payload.trim().startsWith("{")) {
        try {
            payload = JSON.parse(payload);
        } catch (e) {
            // Not JSON; fall through and show it as plain text.
        }
    }
    if (typeof payload === "string") {
        return { title: "", badge: "", paragraphs: toList(payload), evidence: [], recommendations: [] };
    }
    const data = payload || {};
    const rootCause = data.root_cause && typeof data.root_cause === "object" ? data.root_cause : {};
    const contributingSource = typeof data.contributing_source === "string" ? data.contributing_source : "";
    return {
        title: rootCause.summary || data.summary || data.title || "",
        badge: contributingSource && contributingSource !== "Unknown" ? contributingSource : "",
        paragraphs: [
            ...toList(rootCause.description ?? data.description),
            ...toList(data.impact_summary),
        ],
        evidence: toLines(rootCause.evidence ?? data.evidence),
        recommendations: toList(data.recommendation),
        sourceDataset: rootCause.affected_pipeline || "",
        sourceAttribute: rootCause.affected_stage || "",
    };
}

// Vertical ai_insights shape: { quick_view, title, description, recommendation }
const normalizeVerticalAiInsights = (payload) => {
    const data = payload && typeof payload === "object" ? payload : {};
    return {
        title: data.title || "",
        badge: data.quick_view || "",
        paragraphs: toList(data.description),
        evidence: [],
        recommendations: toList(data.recommendation),
        sourceDataset: "",
        sourceAttribute: "",
    };
};

const POPOVER_CODE_PROPS = { className: "ai-popover-code tw-break-all" };
// The hero panels sit outside the .ai-popover styles, so their code chips are styled inline.
const PANEL_CODE_PROPS = {
    style: {
        color: "#FFFFFF",
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        padding: "1px 6px",
        borderRadius: 4,
        fontSize: 12,
        wordBreak: "break-all",
    },
};

// Renders **bold**, `code` and snake_case identifiers without injecting HTML.
export const formatIncidentAiInsightText = (text, codeProps = POPOVER_CODE_PROPS) => {
    if (typeof text !== "string") return text;
    return text.split(INLINE_TOKEN_RE).filter(Boolean).map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) return <b key={i}>{part.slice(2, -2)}</b>;
        if (part.startsWith("`") && part.endsWith("`")) return <code key={i} {...codeProps}>{part.slice(1, -1)}</code>;
        if (SNAKE_CASE_RE.test(part)) return <code key={i} {...codeProps}>{part}</code>;
        return <React.Fragment key={i}>{part}</React.Fragment>;
    });
};
const formatText = (text) => formatIncidentAiInsightText(text);

/** Paragraphs for the hero "QualdoAI Insights" panels. */
export function AiInsightPanelParagraphs({ paragraphs }) {
    return (paragraphs || []).map((paragraph, i) => (
        <p key={i} className="tw-text-[14px] tw-text-[#E2EAFE]/85 tw-mb-4 tw-leading-relaxed">
            {formatIncidentAiInsightText(paragraph, PANEL_CODE_PROPS)}
        </p>
    ));
}

/** Labelled text for the hero panels ("Recommended:", "Evidence:"): first paragraph, the rest behind Show more. */
export function AiInsightPanelRecommendation({ paragraphs, label = "Recommended:", spaced = true }) {
    const [expanded, setExpanded] = React.useState(false);
    const list = paragraphs || [];
    const visible = expanded ? list : list.slice(0, 1);
    return (
        <div className={`tw-text-[14px] tw-text-white tw-leading-normal ${spaced ? "tw-mb-3" : "tw-mb-0"}`}>
            {visible.map((paragraph, i) => (
                <p key={i} className={i === visible.length - 1 ? "tw-mb-0" : "tw-mb-2"}>
                    {i === 0 && <span className="tw-text-white tw-font-semibold tw-mr-[6px]">{label}</span>}
                    {formatIncidentAiInsightText(paragraph, PANEL_CODE_PROPS)}
                </p>
            ))}
            {list.length > 1 && (
                <button
                    type="button"
                    className="tw-mt-2 tw-p-0 tw-border-0 tw-bg-transparent tw-font-semibold tw-cursor-pointer"
                    style={{ color: "#8FB0FF", fontSize: 13, textDecoration: "underline", textUnderlineOffset: 2 }}
                    aria-expanded={expanded}
                    onClick={() => setExpanded(!expanded)}
                >
                    {expanded ? "Show less" : "Show more"}
                </button>
            )}
        </div>
    );
}

const getCacheKey = (request) => (request ? JSON.stringify(request) : null);

/** Stable per-incident key (DQ vs pipeline id) for storing insight results in page state. */
export const getIncidentAiInsightsKey = (item) => getCacheKey(getIncidentAiInsightsRequest(item)) || "unavailable";

/** Resolves to { data } or { error }; never rejects. */
export function fetchIncidentAiInsights(request) {
    if (!request) return Promise.resolve({ error: NOT_AVAILABLE_ERROR });
    const key = getCacheKey(request);
    if (insightsCache.has(key)) return Promise.resolve({ data: insightsCache.get(key) });
    if (pendingRequests.has(key)) return pendingRequests.get(key);

    let apiCall = inflightCalls.get(key);
    if (!apiCall) {
        const call = request.vertical
            ? getVerticalAiInsights(request.params, request.method)
            : getIncidentAiInsights(request);
        apiCall = Promise.resolve(call)
            .then((response) => {
                const failed = !response || (response.code !== undefined && response.code !== 200);
                const payload = response?.response;
                if (failed || payload === null || payload === undefined) {
                    console.error("AI insights request failed:", request, response);
                    return { error: response?.message || DEFAULT_ERROR };
                }
                const data = request.vertical ? normalizeVerticalAiInsights(payload) : normalizeIncidentAiInsights(payload);
                insightsCache.set(key, data);
                return { data };
            })
            .catch((error) => {
                console.error("Error fetching incident AI insights:", error);
                return { error: DEFAULT_ERROR };
            })
            .finally(() => inflightCalls.delete(key));
        inflightCalls.set(key, apiCall);
    }

    // Vertical insights are generated synchronously by the POST call, so wait for it like the hero panel does.
    if (request.vertical) {
        const pendingVertical = apiCall.then((result) => {
            pendingRequests.delete(key);
            return result;
        });
        pendingRequests.set(key, pendingVertical);
        return pendingVertical;
    }

    let timer;
    const timeout = new Promise((resolve) => {
        timer = setTimeout(() => resolve({ error: TIMEOUT_ERROR }), REQUEST_TIMEOUT_MS);
    });

    const pending = Promise.race([apiCall, timeout]).then((result) => {
        clearTimeout(timer);
        pendingRequests.delete(key);
        return result;
    });
    pendingRequests.set(key, pending);
    return pending;
}

/**
 * Popover body for "AI Insights": fetches incident_ai_insights when the popover opens
 * (react-bootstrap mounts overlay content only while it is shown) and renders it in the
 * existing ai-popover design.
 */
export default class IncidentAiInsightContent extends React.Component {
    constructor(props) {
        super(props);
        const key = getCacheKey(props.request);
        const cached = key ? insightsCache.get(key) : null;
        this.state = {
            loading: !!key && !cached,
            data: cached || null,
            error: key ? null : NOT_AVAILABLE_ERROR,
            expanded: false,
            evidenceExpanded: false,
            elapsedSec: 0,
        };
    }

    componentDidMount() {
        this.mounted = true;
        if (this.state.loading) this.load();
    }

    componentDidUpdate(prevProps) {
        const key = getCacheKey(this.props.request);
        if (key === getCacheKey(prevProps.request)) return;
        const cached = key ? insightsCache.get(key) : null;
        this.setState(
            {
                loading: !!key && !cached,
                data: cached || null,
                error: key ? null : NOT_AVAILABLE_ERROR,
                expanded: false,
                evidenceExpanded: false,
                elapsedSec: 0,
            },
            () => {
                if (this.state.loading) this.load();
            }
        );
    }

    componentWillUnmount() {
        this.mounted = false;
        this.stopTimer();
    }

    startTimer = () => {
        this.stopTimer();
        const startedAt = Date.now();
        this.timer = setInterval(() => {
            if (this.mounted) this.setState({ elapsedSec: Math.floor((Date.now() - startedAt) / 1000) });
        }, 1000);
    };

    stopTimer = () => {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
    };

    load = async () => {
        const request = this.props.request;
        this.startTimer();
        const result = await fetchIncidentAiInsights(request);
        if (!this.mounted || getCacheKey(request) !== getCacheKey(this.props.request)) return;
        this.stopTimer();
        if (result.data) {
            this.setState({ loading: false, data: result.data, error: null });
        } else {
            this.setState({ loading: false, error: result.error || DEFAULT_ERROR });
        }
    };

    retry = () => {
        this.setState({ loading: true, error: null, elapsedSec: 0 }, this.load);
    };

    renderCollapsibleBox(label, items, stateKey) {
        if (!items.length) return null;
        const expanded = !!this.state[stateKey];
        const visible = expanded ? items : items.slice(0, 1);
        return (
            <div className="ai-popover-recommended">
                {visible.map((paragraph, i) => (
                    <p key={i} className={i === visible.length - 1 ? "tw-mb-0" : "tw-mb-2"}>
                        {i === 0 && <><b>{label}</b>{" "}</>}
                        {formatText(paragraph)}
                    </p>
                ))}
                {items.length > 1 && (
                    <button
                        type="button"
                        className="ai-popover-show-more"
                        aria-expanded={expanded}
                        onClick={() => this.setState({ [stateKey]: !expanded })}
                    >
                        {expanded ? "Show less" : "Show more"}
                    </button>
                )}
            </div>
        );
    }

    renderSummary() {
        const { loading, error, data, elapsedSec } = this.state;

        if (loading) {
            return (
                <div className="ai-popover__summary">
                    <h6 className="ai-popover-title">AI Explained</h6>
                    <p className="ai-popover-p">
                        {elapsedSec < SLOW_NOTICE_AFTER_SEC
                            ? "Loading AI insights..."
                            : `QualdoAI is analysing this incident. First-time insights can take up to a minute (${elapsedSec}s)...`}
                    </p>
                </div>
            );
        }
        if (error) {
            return (
                <div className="ai-popover__summary">
                    <h6 className="ai-popover-title">AI Explained</h6>
                    <p className="ai-popover-p">{error}</p>
                    {this.props.request && (
                        <button type="button" className="ai-popover-show-more" onClick={this.retry}>
                            Retry
                        </button>
                    )}
                </div>
            );
        }

        const evidence = data?.evidence || [];
        const recommendations = data?.recommendations || [];
        const hasContent = data && (data.title || data.paragraphs.length || evidence.length || recommendations.length || data.badge);

        return (
            <div className="ai-popover__summary">
                <h6 className="ai-popover-title">{data?.title || "AI Explained"}</h6>
                {data?.badge && (
                    <div className="ai-popover-badge">
                        <TriangleAlert size={12} className="tw-shrink-0" />
                        <span>{data.badge}</span>
                    </div>
                )}
                {(data?.paragraphs || []).map((paragraph, i) => (
                    <p className="ai-popover-p" key={i}>{formatText(paragraph)}</p>
                ))}
                {this.renderCollapsibleBox("Evidence:", evidence, "evidenceExpanded")}
                {this.renderCollapsibleBox("Recommended:", recommendations, "expanded")}
                {!hasContent && <p className="ai-popover-p">No AI insights available for this incident yet.</p>}
            </div>
        );
    }

    render() {
        const { data } = this.state;
        const sourceDataset = this.props.sourceDataset || data?.sourceDataset;
        const sourceAttribute = this.props.sourceAttribute || data?.sourceAttribute;

        return (
            <>
                {this.renderSummary()}
                {(sourceDataset || sourceAttribute) && (
                    <>
                        <div className="line-divider"></div>
                        <div className="ai-popover__meta">
                            <h6>Main source</h6>
                            <div className="ai-popover-source-flow">
                                {sourceDataset && (
                                    <>
                                        <FontAwesomeIcon icon={faLayerGroup} className="tw-text-[#E9F0FF]/55 tw-text-xs tw-mr-1.5 tw-inline-block tw-align-middle" />
                                        <span>{sourceDataset}</span>
                                    </>
                                )}
                                {sourceDataset && sourceAttribute && (
                                    <ChevronRight size={12} className="tw-text-white/40 tw-mx-1.5 tw-inline-block tw-align-middle tw-shrink-0" />
                                )}
                                {sourceAttribute && (
                                    <>
                                        <FontAwesomeIcon icon={faTag} className="tw-text-[#E9F0FF]/55 tw-text-xs tw-mr-1.5 tw-inline-block tw-align-middle" />
                                        <span>{sourceAttribute}</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </>
        );
    }
}
